import * as Sentry from '@sentry/browser'
// Tree-shaking doesn't work correctly; import directly.
import {config, logger} from '@bitstillery/common/app'

export type ExceptionContext = Parameters<typeof Sentry.captureException>[1]
export type MessageContext = Parameters<typeof Sentry.captureMessage>[1]
/** The plain-object form of a capture context (`extra`, `tags`, `level`, …), as opposed to a Scope. */
export type CaptureFields = Exclude<Sentry.CaptureContext, Sentry.Scope | ((scope: Sentry.Scope) => Sentry.Scope)>

// Suppress exceptions from tawk d.languageParser.
const ignored_exception_messages = [
    "null is not an object (evaluating 'd.languageParser')",
    'TypeError: d is null',
    "TypeError: Cannot read property 'languageParser' of null",
    'UnhandledRejection: Non-Error promise rejection captured with value', // Error from sentry.browser
]

export class Telemetry {
    enabled = false

    initialize_sentry() {
        if (!config.sentry_dsn || globalThis.__SSR_MODE__) {
            logger.debug('[telemetry] sentry disabled')
            return
        }

        logger.debug('[telemetry] sentry enabled')
        this.enabled = true
        Sentry.init({
            beforeSend: function beforeSend(event, hint) {
                if (hint.originalException && typeof hint.originalException === 'object' && 'message' in hint.originalException) {
                    const {message} = hint.originalException
                    if (typeof message === 'string' && message && ignored_exception_messages.some((i) => i.startsWith(message))) {
                        return null
                    }
                }
                return event
            },
            dsn: config.sentry_dsn,
            environment: config.env,
            integrations: [
                Sentry.captureConsoleIntegration({
                    levels: ['warn', 'error'],
                }),
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    blockAllMedia: false,
                    maskAllText: false,
                }),
            ],
            // Trace all page loads, reduce this when there is too much data
            // Coming in to Sentry.
            tracesSampleRate: 1,

            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1,

            normalizeDepth: 11,
            release: `${process.env.MSI_VERSION}`,
            dist: config.sentry_dist,

            tracePropagationTargets: [config.api_host, config.api_host_new],
        })
    }

    capture_exception(error: Error, context?: ExceptionContext) {
        if (!this.enabled) {
            return
        }
        Sentry.captureException(error, context)
    }

    capture_message(message: string, context?: MessageContext) {
        if (!this.enabled) {
            return
        }
        Sentry.captureMessage(message, context)
    }

    /** Set the user logged with the Sentry issues. Only the user artkey is logged for privacy reasons. */
    set_user(user: {artkey?: number; [field: string]: unknown} | null | undefined) {
        if (!this.enabled) {
            return
        }
        // Logging out passes no user; clear it so later events aren't attributed to them.
        if (!user) {
            Sentry.setUser(null)
            return
        }
        // Only report user_artkey in sentry.
        Sentry.setUser(user.artkey === undefined ? {} : {id: user.artkey})
    }
}
